/**
 * Security Utilities
 * Provides sanitization and validation functions for XSS prevention
 */

/**
 * Sanitize HTML string to prevent XSS attacks
 * @param {string} html - HTML string to sanitize
 * @returns {string} - Sanitized text content
 */
function sanitizeHTML(html) {
  if (!html || typeof html !== 'string') return '';
  
  const temp = document.createElement('div');
  temp.textContent = html;
  return temp.innerHTML;
}

/**
 * Safely set innerHTML with sanitization
 * @param {HTMLElement} element - Target element
 * @param {string} html - HTML content to set
 */
function safeSetInnerHTML(element, html) {
  if (!element || !html) return;
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  element.innerHTML = '';
  
  Array.from(doc.body.childNodes).forEach(node => {
    element.appendChild(node.cloneNode(true));
  });
}

/**
 * Validate and sanitize data from localStorage
 * @param {string} key - localStorage key
 * @returns {string|null} - Validated value or null
 */
function getValidatedLocalStorage(key) {
  try {
    const value = localStorage.getItem(key);
    if (!value) return null;
    
    // Remove any potential script tags or dangerous content
    const sanitized = value.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    const cleaned = sanitized.replace(/[<>]/g, '');
    
    // Validate it's alphanumeric with basic chars only
    if (!/^[a-zA-Z0-9\s\-_]+$/.test(cleaned)) {
      console.warn(`Invalid characters in localStorage key: ${key}`);
      return null;
    }
    
    return cleaned;
  } catch (e) {
    console.error('Error reading from localStorage:', e);
    return null;
  }
}

/**
 * Safely set data to localStorage with validation
 * @param {string} key - localStorage key
 * @param {string} value - Value to store
 * @returns {boolean} - Success status
 */
function setValidatedLocalStorage(key, value) {
  try {
    if (!value || typeof value !== 'string') return false;
    
    // Sanitize value
    const cleaned = value.replace(/[<>]/g, '').trim();
    
    // Validate length
    if (cleaned.length > 100) {
      console.warn('Value too long for localStorage');
      return false;
    }
    
    // Validate alphanumeric
    if (!/^[a-zA-Z0-9\s\-_]+$/.test(cleaned)) {
      console.warn('Invalid characters in value');
      return false;
    }
    
    localStorage.setItem(key, cleaned);
    return true;
  } catch (e) {
    console.error('Error writing to localStorage:', e);
    return false;
  }
}

/**
 * Escape HTML entities
 * @param {string} text - Text to escape
 * @returns {string} - Escaped text
 */
function escapeHTML(text) {
  if (!text) return '';
  
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  
  return String(text).replace(/[&<>"']/g, char => map[char]);
}

/**
 * Validate URL to prevent javascript: and data: URLs
 * @param {string} url - URL to validate
 * @returns {boolean} - Is valid URL
 */
function isValidURL(url) {
  if (!url) return false;
  
  try {
    const parsed = new URL(url, window.location.origin);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
}

// Export utilities
window.SecurityUtils = {
  sanitizeHTML,
  safeSetInnerHTML,
  getValidatedLocalStorage,
  setValidatedLocalStorage,
  escapeHTML,
  isValidURL
};

